import { getEnvVarsQPR } from '@cdab/scania/qpr/env-vars'
import type {
  AuditsRealTimeService as AuditsRealTimeServiceDefinition,
  CreateDeviationResponse,
  OnReconnectedCallback,
  RealTimeCallbacks,
  SetNoteData,
  SetNoteDataCommon,
  Unsub,
  UpdateCheckPointScoreAsync,
  UpdateDeviationData
} from '@cdab/scania/qpr/interactor'
import type { Audit, Deviation } from '@cdab/scania/qpr/schema'
import { formatISODate } from '@cdab/utils'
import * as signalR from '@microsoft/signalr'
import invariant from 'tiny-invariant'
import { v4 as uuidv4 } from 'uuid'

import type {
  AuditPointUpdateDto,
  CheckPointUpdateDto,
  DeviationCreateDto,
  DeviationCreateResponseDto,
  DeviationDeleteDto,
  DeviationUpdateDto,
  DeviationWithoutActionsCreateDto,
  DeviationWithoutActionsCreateResponseDto,
  DeviationWithoutActionsUpdateDto,
  NoteCreateUpdateDto
} from './generated-swagger-client'

export class AuditsRealTimeService implements AuditsRealTimeServiceDefinition {
  private callbacks = new Map<
    Audit['id'],
    Map<RealTimeCallbacks['id'], RealTimeCallbacks>
  >()
  private onReconnectedCallbacks: OnReconnectedCallback[] = []

  private static instance: AuditsRealTimeService | undefined = undefined

  private connection!: signalR.HubConnection

  public get disposed(): boolean {
    return this._disposed
  }

  private _disposed = false
  private isReconnecting = false
  private reconnectTimeout?: NodeJS.Timeout

  private readonly retryMs = 5000
  private hasConnectedFirstTime = false
  private signalRUrl: string
  private connectionMonitorInterval?: NodeJS.Timeout
  private lastPingTime = Date.now()
  private readonly pingIntervalMs = 10_000

  public constructor(signalRUrl: string) {
    this.signalRUrl = signalRUrl

    this.createConnection()
    AuditsRealTimeService.instance = this
    this.start()

    this.startConnectionMonitoring()
  }

  private createConnection = () => {
    if (this.connection) {
      this.disposeConnection()
    }

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(this.signalRUrl)
      .configureLogging(
        getEnvVarsQPR().NODE_ENV === 'development'
          ? signalR.LogLevel.Information
          : signalR.LogLevel.None
      )
      .build()

    this.setupEventHandlers()
    this.setupConnectionHandlers()
  }

  private setupConnectionHandlers = () => {
    this.connection.onreconnecting(() => {
      this.isReconnecting = true
    })

    this.connection.onreconnected(() => {
      this.isReconnecting = false
      if (this.hasConnectedFirstTime) {
        this.onReconnect()
      }
    })

    this.connection.onclose(() => {
      if (this._disposed) {
        return
      }

      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout)
      }

      if (!this.isReconnecting) {
        this.reconnectTimeout = setTimeout(() => {
          this.reconnect()
        }, this.retryMs)
      }
    })
  }

  private setupEventHandlers = () => {
    this.connection.on(
      'AuditPointCheckBoxChanged',
      (auditUpdate: AuditPointUpdateDto) => {
        const { auditId, callIdentifier, id, isPointCheckedYes } = auditUpdate

        const score =
          typeof isPointCheckedYes === 'undefined' ? null : isPointCheckedYes

        this.handleCallbacks(auditId, callbacks => {
          callbacks.onAuditPointScoreChange(
            auditId,
            id,
            score,
            callIdentifier,
            new Date(auditUpdate.created)
          )
        })
      }
    )
    this.connection.on(
      'CheckPointCheckBoxChanged',
      (checkpointUpdate: CheckPointUpdateDto) => {
        const { created, ...checkpointUpdateData } = checkpointUpdate

        this.handleCallbacks(checkpointUpdate.auditId, callback => {
          callback.onCheckPointScoreChange({
            created: new Date(created),
            ...checkpointUpdateData
          })
        })
      }
    )
    this.connection.on(
      'NoteSaveOrUpdateChanged',
      (noteUpdate: NoteCreateUpdateDto) => {
        const { pledgeId, auditPointId, checkPointId, created, ...rest } =
          noteUpdate
        const common: SetNoteDataCommon = {
          ...rest,
          created: new Date(created)
        }
        let noteData: SetNoteData

        if (typeof pledgeId === 'number') {
          noteData = {
            ...common,
            pledgeId,
            auditPointId: null,
            checkPointId: null
          }
        } else if (typeof auditPointId === 'number') {
          noteData = {
            ...common,
            pledgeId: null,
            auditPointId,
            checkPointId: null
          }
        } else if (typeof checkPointId === 'number') {
          noteData = {
            ...common,
            pledgeId: null,
            auditPointId: null,
            checkPointId
          }
        } else {
          noteData = {
            ...common,
            pledgeId: null,
            auditPointId: null,
            checkPointId: null
          }
        }

        this.handleCallbacks(noteUpdate.auditId, callback => {
          callback.onNoteChange(noteData)
        })
      }
    )
    this.connection.on(
      'DeviationWithoutActionUpdateChanged',
      (deviationUpdate: DeviationWithoutActionsUpdateDto) => {
        const { created, ...data } = deviationUpdate
        this.handleCallbacks(deviationUpdate.auditId, callback => {
          callback.onDeviationUpdate({
            deviation: {
              ...data,
              auditPointId: deviationUpdate.auditPointId || null,
              created: new Date(created)
            },
            withActionPlan: false
          })
        })
      }
    )
    this.connection.on(
      'DeviationUpdateChanged',
      (deviationUpdate: DeviationUpdateDto) => {
        const { created, approvalDate, ...data } = deviationUpdate
        this.handleCallbacks(deviationUpdate.auditId, callback => {
          callback.onDeviationUpdate({
            deviation: {
              ...data,
              created: new Date(created),
              expirationDate: data.expirationDate ?? null,
              approvalDate: approvalDate ?? null,
              auditPointId: deviationUpdate.auditPointId || null
            },
            withActionPlan: true
          })
        })
      }
    )
    this.connection.on(
      'DeviationSaveChanged',
      (deviation: DeviationCreateResponseDto) => {
        const { approvalDate, expirationDate, id } = deviation
        const { auditId } = deviation

        if (typeof id !== 'number') throw new Error('No id')

        this.handleCallbacks(auditId, callback => {
          callback.onCreateDeviation(
            deviation.callIdentifier,
            {
              ...deviation,
              clientGuid: uuidv4(),
              deviationWithoutActions: false,
              approvalDate: approvalDate ? new Date(approvalDate) : null,
              expirationDate:
                expirationDate != null ? new Date(expirationDate) : null,
              id,
              actionPlanId: deviation.actionPlanId,
              auditPointId: deviation.auditPointId || null,
              draft: deviation.draft ?? false
            },
            new Date(deviation.created)
          )
        })
      }
    )
    this.connection.on(
      'DeviationWithoutActionSaveChanged',
      (deviation: DeviationWithoutActionsCreateResponseDto) => {
        this.handleCallbacks(deviation.auditId, callback => {
          callback.onCreateDeviation(
            deviation.callIdentifier,
            {
              ...deviation,
              clientGuid: uuidv4(),
              deviationWithoutActions: true,
              approvalDate: null,
              expirationDate: null,
              proposedActions: null,
              responsible: null,
              auditPointId: deviation.auditPointId || null,
              actionPlanId: deviation.actionPlanId
            },
            new Date(deviation.created)
          )
        })
      }
    )
    this.connection.on(
      'DeviationDeleteChanged',
      (deviationDeleteDto: DeviationDeleteDto) => {
        const { auditId, callIdentifier, id } = deviationDeleteDto

        this.handleCallbacks(deviationDeleteDto.auditId, callback => {
          callback.onDeviationDelete({
            auditId,
            callIdentifier,
            deviationId: id
          })
        })
      }
    )
  }

  private disposeConnection = async () => {
    if (this.connection) {
      try {
        this.callbacks.forEach((callbackMap, auditId) => {
          callbackMap.forEach((_, callbackId) => {
            this.connection.off(callbackId)
          })
        })

        if (this.connection.state !== signalR.HubConnectionState.Disconnected) {
          await this.connection.stop()
        }
      } catch (error) {
        console.error('Error disposing connection:', error)
      }
    }
  }

  private reconnect = async () => {
    if (this._disposed) {
      return
    }

    this.isReconnecting = true

    this.createConnection()

    try {
      await this.connection.start()

      if (this.hasConnectedFirstTime) {
        this.onReconnect()
      }

      this.isReconnecting = false
    } catch (error) {
      console.error('Reconnection failed:', error)
      this.isReconnecting = false

      if (this.reconnectTimeout) {
        clearTimeout(this.reconnectTimeout)
      }

      this.reconnectTimeout = setTimeout(() => {
        this.reconnect()
      }, this.retryMs)
    }
  }

  private start = async () => {
    if (this._disposed) {
      return
    }

    try {
      await this.connection.start()

      if (this.hasConnectedFirstTime) {
        this.onReconnect()
      }

      this.hasConnectedFirstTime = true
    } catch (error) {
      console.error('Failed to start connection:', error)

      if (!this._disposed) {
        if (this.reconnectTimeout) {
          clearTimeout(this.reconnectTimeout)
        }

        this.reconnectTimeout = setTimeout(() => {
          this.start()
        }, this.retryMs)
      }
    }
  }

  private handleCallbacks = (
    auditId: Audit['id'],
    callbackFn: (callbacks: RealTimeCallbacks) => void
  ) => {
    this.callbacks.get(auditId)?.forEach(callbackFn)
  }

  public AddOnReconnectedCallback = (callback: OnReconnectedCallback) => {
    if (this.onReconnectedCallbacks.includes(callback)) return

    this.onReconnectedCallbacks.push(callback)
  }

  public UpdateCheckPointScore = async (update: UpdateCheckPointScoreAsync) => {
    const ok: boolean = await this.connection.invoke(
      'UpdateCheckPointCheckBoxAsync',
      {
        ...update
      }
    )

    return ok
  }

  private onReconnect = () => {
    this.onReconnectedCallbacks.forEach(callback => callback())
  }

  public UpdateAuditPointScore = async (
    auditId: number,
    auditPointId: number,
    score: boolean | null,
    callIdentifier: string,
    actionCreated: Date
  ) => {
    const updateParams: AuditPointUpdateDto = {
      auditId,
      id: auditPointId,
      isPointCheckedYes: score,
      callIdentifier,
      created: actionCreated.toJSON()
    }

    const ok: boolean = await this.connection.invoke(
      'UpdateAuditPointCheckBoxAsync',
      updateParams
    )

    return ok
  }

  public ListenTo = async (
    auditId: number,
    callbacks: RealTimeCallbacks
  ): Promise<Unsub> => {
    if (callbacks) {
      if (!this.callbacks.has(auditId)) this.callbacks.set(auditId, new Map())

      const callbacksMap = this.callbacks.get(auditId)
      invariant(callbacksMap)

      if (!callbacksMap.has(callbacks.id)) {
        callbacksMap.set(callbacks.id, callbacks)
      }
    }
    await this.connection.invoke('ListenToAudit', { auditId })

    return () => {
      this.connection.invoke('StopListenToAudit', { auditId })
      if (callbacks) {
        const auditCallbacks = this.callbacks.get(auditId)
        if (!auditCallbacks) return
        if (auditCallbacks.has(callbacks.id)) {
          auditCallbacks.delete(callbacks.id)
        }
      }
    }
  }

  public SetNote = async (setNoteData: SetNoteData) => {
    const data: NoteCreateUpdateDto = {
      ...setNoteData,
      created: setNoteData.created.toJSON()
    }

    const ok: boolean = await this.connection.invoke(
      'SaveOrUpdateNoteAsync',
      data
    )

    return ok
  }

  public async UpdateDeviation(deviationData: UpdateDeviationData) {
    const methodName = deviationData.withActionPlan
      ? 'UpdateDeviationAsync'
      : 'UpdateDeviationWithoutActionsAsync'

    let data: DeviationUpdateDto | DeviationWithoutActionsUpdateDto

    const { created, ...deviation } = deviationData.deviation

    if (deviationData.withActionPlan) {
      const { expirationDate, approvalDate } = deviationData.deviation

      data = {
        ...deviation,
        created: created.toJSON(),
        expirationDate: expirationDate
          ? formatISODate(new Date(expirationDate))
          : null,
        approvalDate: approvalDate
          ? formatISODate(new Date(approvalDate))
          : null
      }
    } else {
      data = {
        ...deviation,
        created: created.toJSON()
      }
    }

    const result = await this.connection.invoke(methodName, data)

    return result
  }

  public async CreateDeviation(
    callIdentifier: string,
    deviation: Deviation,
    created: Date
  ): Promise<CreateDeviationResponse> {
    if (this._disposed) {
      throw new Error('SignalR connection is disposed')
    }

    if (this.connection.state !== signalR.HubConnectionState.Connected) {
      throw new Error('SignalR connection is not in Connected state')
    }

    let methodName: string = deviation.deviationWithoutActions
      ? 'SaveDeviationWithoutActionAsync'
      : 'SaveDeviationAsync'

    let createDeviation: DeviationCreateDto | DeviationWithoutActionsCreateDto
    if (deviation.deviationWithoutActions) {
      if (!deviation.auditPointId && !deviation.draft) {
        throw new Error(
          'Audit Point Id is required when creating a deviation without action plan!'
        )
      }

      methodName = 'SaveDeviationWithoutActionAsync'
      createDeviation = {
        auditPointNumber: deviation.auditPointNumber,
        auditId: deviation.auditId,
        auditPointId: deviation.auditPointId ?? null,
        callIdentifier,
        deviation: deviation.deviation,
        created: created.toJSON(),
        draft: deviation.draft ?? false
      }
    } else {
      const { approvalDate, expirationDate, ...rest } = deviation

      if (!expirationDate && !rest.draft) {
        throw new Error(
          'Expiration date is requried when creating deviation with action plan!'
        )
      }

      if (
        !rest.draft &&
        (!rest.auditId ||
          !rest.auditPointId ||
          !rest.deviation ||
          rest.deviationWithoutActions === null ||
          rest.proposedActions === null ||
          rest.responsible === null)
      ) {
        throw new Error(
          'There were missing required fields when creating a deviation!'
        )
      }

      methodName = 'SaveDeviationAsync'
      createDeviation = {
        auditId: rest.auditId,
        auditPointId: rest.auditPointId,
        deviation: rest.deviation,
        proposedActions: rest.proposedActions ?? '',
        responsible: rest.responsible ?? '',
        auditPointNumber: rest.auditPointNumber,
        callIdentifier,
        created: created.toJSON(),
        expirationDate: expirationDate ? formatISODate(expirationDate) : null,
        approvalDate: approvalDate ? formatISODate(approvalDate) : null,
        draft: rest.draft ?? false
      }
    }

    try {
      const response: string = await this.connection.invoke(
        methodName,
        createDeviation
      )
      const parsed = JSON.parse(response)

      return parsed
    } catch (error) {
      if (
        error instanceof Error &&
        error.message ===
          'Server timeout elapsed without receiving a message from the server.'
      ) {
        this.onReconnect()
      }
      throw error
    }
  }

  public DeleteDeviation = async (
    callIdentifier: string,
    created: Date,
    auditId: number,
    deviationId: number
  ): Promise<boolean> => {
    const deleteDeviation: DeviationDeleteDto = {
      auditId,
      callIdentifier,
      created: created.toJSON(),
      id: deviationId
    }

    const ok = await this.connection.invoke(
      'DeleteDeviationAsync',
      deleteDeviation
    )

    return ok
  }

  private startConnectionMonitoring = () => {
    if (this.connectionMonitorInterval) {
      clearInterval(this.connectionMonitorInterval)
    }

    this.connectionMonitorInterval = setInterval(() => {
      this.checkConnectionHealth()
    }, this.pingIntervalMs)
  }

  private checkConnectionHealth = async () => {
    if (this._disposed || this.isReconnecting) {
      return
    }

    if (this.connection.state !== signalR.HubConnectionState.Connected) {
      return
    }

    this.lastPingTime = Date.now()
  }

  public dispose = async () => {
    this._disposed = true

    if (this.connectionMonitorInterval) {
      clearInterval(this.connectionMonitorInterval)
      this.connectionMonitorInterval = undefined
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout)
    }

    this.callbacks.clear()
    this.onReconnectedCallbacks = []

    await this.disposeConnection()

    AuditsRealTimeService.instance = undefined
  }

  public static reopen(signalRUrl: string): AuditsRealTimeService {
    if (AuditsRealTimeService.instance) {
      AuditsRealTimeService.instance.dispose()
    }
    return new AuditsRealTimeService(signalRUrl)
  }
}
